import { generateThumbnailId } from "@models/data/sceneDto";
import type { SceneViewState } from "@models/data/sceneInfo";
import { isSceneViewSet } from "@models/data/sceneInfo";
import { Ioc } from "@core/ioc/ioc";
import type { IStorageModel } from "@models/storage/IStorageModel";
import type { IProjectListModel } from "./IProjectListModel";
import { PROJECT_LIST_MODEL } from "./IProjectListModel";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelThumbnail` — partial of {@link ProjectModel} owning scene
 * thumbnail capture. Port of `Models/ProjectModel/ProjectModel_Thumbnails.cs`
 * (`UpdateThumbnailForActiveScene` → `UpdateThumbnailAsync`) plus the
 * persistence order from `ProjectDataModel.UpdateThumbnailAsync`.
 *
 * Unity order (kept exactly), serialized per scene like Unity's
 * `AsyncNamedLock(sceneID)`:
 *   1. bump the SCENE row's `ModifiedTimestamp` (this is the cache-busting
 *      signal — both clients key their thumbnail caches on it),
 *   2. bump the PROJECT row's `ModifiedTimestamp` (grid re-sort),
 *   3. S3 `PutObject` the PNG under
 *      `…/{projectId}/thumbnails/{thumbnailId}` (no `.png` suffix),
 *   4. fire-and-forget `update_thumbnail` (tc 39) — a pure invalidation
 *      ping carrying no bytes; peers refetch.
 *
 * There is deliberately NO `ThumbnailID` minting or scene-row attribute
 * write: Unity's key is always `generateThumbnailId(sceneId)` and the S3
 * object is overwritten in place (the row attribute is vestigial).
 *
 * Scene duplication reuses this persistence path after copying the source
 * thumbnail bytes. That mirrors Unity's `DuplicateThumbnailAsync`: clone the
 * existing preview rather than recapturing whichever scene is active when the
 * asynchronous duplicate finishes.
 *
 * The pixels come from a host registered by the viewport
 * ({@link IPreviewCaptureHost}) so this model never imports Three.js.
 * With no host (headless / no viewport mounted) capture no-ops.
 */

/**
 * Renderer-side capture seam (registered by `ViewportView`). Mirrors Unity's
 * single `IAssetPreviewUtility`, which serves both preview kinds: the scene
 * card (`RenderScene`) and the asset-library thumbnail (`RenderAsset`, used by
 * `ProjectModelUpload`).
 *
 * Its registration doubles as the model's "a viewport is mounted" signal for
 * scene loading (`ProjectModelSceneLoad` → "The build host"), because the
 * viewport attaches it and mounts its renderer in the same `onMounted`. Keep
 * the two together if either moves.
 */
export interface IPreviewCaptureHost {
  /**
   * PNG bytes of the active scene, or `null` when unavailable.
   *
   * `view` is the scene's authored camera view when it has one, else `null`.
   * this: an authored view shoots the card from that pose, otherwise the
   * renderer copies the live editor camera. Passed in rather than read by the
   * renderer so the decision stays in the model — and because it is the whole
   * visible payoff of `FinishSceneView` refreshing the card after a Set Scene
   * View.
   */
  captureActiveSceneThumbnailAsync(
    view: SceneViewState | null,
  ): Promise<Uint8Array | null>;
  /**
   * True while the renderer is still building the active scene — its glTF
   * downloads are NOT covered by `m_sceneEntityLoads` (which tracks only the
   * Dynamo row fetch), so capturing now can persist a card that is missing a
   * just-placed asset.
   */
  isSceneBuildInProgress(): boolean;
  /**
   * 512×512 PNG bytes of a single asset rendered on its own (Unity
   * `RenderAssetPreview`), or `null` when the asset isn't in the render cache.
   */
  captureAssetPreviewAsync(assetId: string): Promise<Uint8Array | null>;
}

export class ProjectModelThumbnail {
  /**
   * Port of Unity `ProjectModel_ScenesManagement.DuplicateThumbnailAsync`.
   * Prefer the model-owned URL when the source was captured in this session;
   * otherwise read the source object from S3. The target is persisted through
   * the same timestamp/upload/event sequence as a freshly captured thumbnail.
   */
  async duplicateThumbnailAsync(
    this: ProjectModel,
    sourceSceneId: string,
    targetSceneId: string,
  ): Promise<void> {
    const state = this as unknown as ThumbnailInternals;
    const ctx = state.getStorageContext();
    if (!ctx) return;

    let sourceUrl = this.getScene(sourceSceneId)?.thumbnailUrl ?? null;
    let ownsSourceUrl = false;
    try {
      if (!sourceUrl) {
        const sourceThumbnailId = generateThumbnailId(sourceSceneId);
        sourceUrl = await ctx.storage.getProjectThumbnailBlobUrl(
          ctx.customerId,
          ctx.projectId,
          sourceThumbnailId,
        );
        if (!sourceUrl && sourceThumbnailId !== sourceSceneId) {
          sourceUrl = await ctx.storage.getProjectThumbnailBlobUrl(
            ctx.customerId,
            ctx.projectId,
            sourceSceneId,
          );
        }
        ownsSourceUrl = sourceUrl?.startsWith("blob:") === true;
      }
      if (!sourceUrl) {
        console.warn(
          `[ProjectModel] duplicate thumbnail skipped (sourceScene=${sourceSceneId}): source thumbnail not found`,
        );
        return;
      }

      const response = await fetch(sourceUrl);
      if (!response.ok) {
        throw new Error(`thumbnail read failed with HTTP ${response.status}`);
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      if (bytes.length === 0) {
        console.warn(
          `[ProjectModel] duplicate thumbnail skipped (sourceScene=${sourceSceneId}): source thumbnail was empty`,
        );
        return;
      }

      await persistSceneThumbnailBytes(this, state, ctx, targetSceneId, bytes);
    } catch (err) {
      // Keep the completed scene duplicate usable if its preview copy fails,
      // matching the non-fatal contract of every other web thumbnail write.
      console.warn(
        `[ProjectModel] duplicate thumbnail failed (sourceScene=${sourceSceneId}, targetScene=${targetSceneId}):`,
        err,
      );
    } finally {
      if (ownsSourceUrl && sourceUrl) URL.revokeObjectURL(sourceUrl);
    }
  }

  /**
   * Unity `UpdateThumbnailForActiveScene` + `UpdateThumbnailAsync`. Skips
   * when a capture for the scene is already in flight (Unity's per-scene
   * named lock) or while the scene's entities are still loading (Unity skips
   * on `m_isChangingActiveScene` — capturing mid-load would persist an empty
   * or half-populated card).
   */
  async updateThumbnailForActiveSceneAsync(this: ProjectModel): Promise<void> {
    const state = this as unknown as ThumbnailInternals;
    // Workflow playback applies transient scene state which may be reverted on
    // step/scene boundaries. Unity's replay/AI mutation path does not refresh
    // scene cards for those writes, so do not capture or upload them on Web.
    if (
      this.isSceneStartPlayModeEnabled
      || this.isWorkflowPlaybackMutationScopeActive
    ) return;

    const scene = this.activeScene;
    if (!scene) return;
    const sceneId = scene.id;
    const projectId = this.activeProjectId;
    if (!projectId) return;
    if (state.m_thumbnailCaptures.has(sceneId)) {
      state.m_pendingThumbnailRefreshes.set(sceneId, projectId);
      return;
    }
    if (state.m_sceneEntityLoads.has(sceneId)) {
      state.m_pendingThumbnailRefreshes.set(sceneId, projectId);
      this.scheduleSceneThumbnail(sceneId, projectId);
      return;
    }

    const host = state.m_previewCaptureHost;
    if (!host) {
      state.m_pendingThumbnailRefreshes.set(sceneId, projectId);
      return;
    }

    // Wait out an in-flight renderer build (asset downloads) rather than
    // uploading a half-built card. Bounded so a wedged renderer can't retry
    // forever; giving up logs rather than persisting a wrong thumbnail.
    if (host.isSceneBuildInProgress()) {
      const attempt = (state.m_thumbnailBuildWaits.get(sceneId) ?? 0) + 1;
      if (attempt > K_MAX_BUILD_WAITS) {
        state.m_thumbnailBuildWaits.delete(sceneId);
        console.warn(
          `[ProjectModel] thumbnail capture skipped (scene=${sceneId}): renderer still building after ${K_MAX_BUILD_WAITS} retries`,
        );
        return;
      }
      state.m_thumbnailBuildWaits.set(sceneId, attempt);
      this.scheduleActiveSceneThumbnail();
      return;
    }
    state.m_thumbnailBuildWaits.delete(sceneId);

    const run = (async () => {
      // Unity `RenderPreview`'s `IsSceneViewSet(scene)` branch — the OR-form
      // predicate, not `isSceneViewResolvable`.
      const sceneView = this.activeScene?.sceneView ?? null;
      const bytes = await host.captureActiveSceneThumbnailAsync(
        sceneView && isSceneViewSet(sceneView) ? sceneView : null,
      );
      if (!bytes || bytes.length === 0) return;
      // The scene may have changed while the PNG encoded.
      if (
        this.activeScene?.id !== sceneId
        || this.isSceneStartPlayModeEnabled
        || this.isWorkflowPlaybackMutationScopeActive
      ) return;

      const ctx = state.getStorageContext();
      if (!ctx) return;
      try {
        await persistSceneThumbnailBytes(this, state, ctx, sceneId, bytes);
      } catch (err) {
        // Unity catches + logs every thumbnail write failure and never
        // throws — a stale card must not break the edit that triggered it.
        console.warn(`[ProjectModel] thumbnail update failed (scene=${sceneId}):`, err);
      }
    })();

    state.m_thumbnailCaptures.set(sceneId, run);
    try {
      await run;
    } finally {
      state.m_thumbnailCaptures.delete(sceneId);
      const pendingProjectId = state.m_pendingThumbnailRefreshes.get(sceneId);
      if (
        pendingProjectId
        && this.activeSceneId === sceneId
        && this.activeProjectId === pendingProjectId
        && !this.isSceneStartPlayModeEnabled
      ) {
        state.m_pendingThumbnailRefreshes.delete(sceneId);
        this.scheduleSceneThumbnail(sceneId, pendingProjectId);
      }
    }
  }

  /**
   * Debounced trigger for the LOCAL mutation paths (Unity calls
   * `UpdateThumbnailForActiveScene` from ~20 ViewModel action sites with no
   * debounce; the web coalesces because a single web action can emit several
   * model events, and a burst of drags would otherwise re-render + re-upload
   * per frame). Deliberately NOT called from the inbound apply paths: Unity
   * skips uploading for a peer's change — "the thumbnail must be updated by
   * the application that made the change".
   */
  scheduleActiveSceneThumbnail(this: ProjectModel): void {
    const state = this as unknown as ThumbnailInternals;
    if (this.isSceneStartPlayModeEnabled) {
      if (state.m_thumbnailTimer !== null) {
        window.clearTimeout(state.m_thumbnailTimer);
        state.m_thumbnailTimer = null;
      }
      return;
    }
    // The timer must remember WHICH scene/project the edit belonged to:
    // otherwise switching scenes inside the debounce window retargets the
    // capture, so the edited scene's card is never refreshed AND the newly
    // opened scene gets a spurious timestamp bump + S3 write + broadcast.
    const sceneId = this.activeSceneId;
    const projectId = this.activeProjectId;
    if (!sceneId || !projectId) return;
    this.scheduleSceneThumbnail(sceneId, projectId);
  }

  scheduleSceneThumbnail(
    this: ProjectModel,
    sceneId: string,
    projectId: string,
  ): void {
    const state = this as unknown as ThumbnailInternals;
    if (state.m_thumbnailTimer !== null) window.clearTimeout(state.m_thumbnailTimer);
    state.m_thumbnailTimer = window.setTimeout(() => {
      state.m_thumbnailTimer = null;
      if (this.activeSceneId !== sceneId || this.activeProjectId !== projectId) return;
      void this.updateThumbnailForActiveSceneAsync();
    }, K_THUMBNAIL_DEBOUNCE_MS);
  }

  /** Registers the renderer-side capture seam (called by `ViewportView`). */
  attachPreviewCaptureHost(
    this: ProjectModel,
    host: IPreviewCaptureHost | null,
  ): void {
    const state = this as unknown as ThumbnailInternals;
    state.m_previewCaptureHost = host;
    if (!host && state.m_thumbnailTimer !== null) {
      window.clearTimeout(state.m_thumbnailTimer);
      state.m_thumbnailTimer = null;
      const sceneId = this.activeSceneId;
      const projectId = this.activeProjectId;
      if (sceneId && projectId) {
        state.m_pendingThumbnailRefreshes.set(sceneId, projectId);
      }
    }
    if (host) {
      const sceneId = this.activeSceneId;
      const projectId = sceneId ? state.m_pendingThumbnailRefreshes.get(sceneId) : null;
      if (sceneId && projectId && this.activeProjectId === projectId) {
        state.m_pendingThumbnailRefreshes.delete(sceneId);
        this.scheduleSceneThumbnail(sceneId, projectId);
      }
      // The same registration tells a waiting scene-load session that a
      // renderer now exists (`ProjectModelSceneLoad` → "The build host").
      this.noteSceneBuildHostAttached();
    }
  }
}

/** Coalesce window for local edits (see `scheduleActiveSceneThumbnail`). */
const K_THUMBNAIL_DEBOUNCE_MS = 1_200;
/** Re-arm limit while waiting for the renderer (≈ 12 s at the debounce above). */
const K_MAX_BUILD_WAITS = 10;

interface ThumbnailStorageContext {
  storage: IStorageModel;
  customerId: string;
  projectId: string;
}

/**
 * Shared persistence tail for renderer captures and duplicate-scene copies.
 * Keeping one owner prevents the timestamp/cache/event ordering from drifting.
 */
async function persistSceneThumbnailBytes(
  project: ProjectModel,
  state: ThumbnailInternals,
  ctx: ThumbnailStorageContext,
  sceneId: string,
  bytes: Uint8Array,
): Promise<void> {
  const { modifiedAt } = await ctx.storage.updateSceneModifiedTimestamp(
    ctx.customerId,
    ctx.projectId,
    sceneId,
  );
  const { modifiedAt: projectModifiedAt } =
    await ctx.storage.updateCustomerProjectModifiedDate(
      ctx.customerId,
      ctx.projectId,
    );
  await ctx.storage.putThumbnailObject(
    ctx.customerId,
    ctx.projectId,
    generateThumbnailId(sceneId),
    bytes,
  );

  // Local invalidation: the card's cache key includes the scene's
  // `modifiedAt`, so mirror the exact timestamp we just wrote. Also attach the
  // fresh blob so the card updates without a refetch.
  const current =
    project.activeProjectId === ctx.projectId ? project.getScene(sceneId) : null;
  if (current) {
    const previousUrl = current.thumbnailUrl;
    const url = URL.createObjectURL(
      new Blob([bytes.slice()], { type: "image/png" }),
    );
    state.applySceneMetadataChanges({ ...current, modifiedAt, thumbnailUrl: url });
    project.onSceneThumbnailChanged.emit({ sceneId, thumbnailUrl: url });
    if (previousUrl?.startsWith("blob:")) URL.revokeObjectURL(previousUrl);
  }

  state.applyProjectModifiedDate(ctx.projectId, projectModifiedAt);
  if (Ioc.isRegistered<IProjectListModel>(PROJECT_LIST_MODEL)) {
    Ioc.resolve<IProjectListModel>(PROJECT_LIST_MODEL).applyProjectModifiedDate(
      ctx.projectId,
      projectModifiedAt,
    );
  }

  // Unity sends the event only after a successful non-null upload. Stamp the
  // captured project explicitly in case the user navigated away mid-upload.
  project.publishLiveProjectEvent("update_thumbnail", {
    projectId: ctx.projectId,
    ProjectId: ctx.projectId,
    sceneId,
    SceneID: sceneId,
    SceneId: sceneId,
  });
}

/** Protected `ProjectModel` state this partial reaches (mixin-cast pattern). */
interface ThumbnailInternals {
  m_previewCaptureHost: IPreviewCaptureHost | null;
  m_thumbnailTimer: number | null;
  readonly m_thumbnailCaptures: Map<string, Promise<void>>;
  readonly m_pendingThumbnailRefreshes: Map<string, string>;
  readonly m_thumbnailBuildWaits: Map<string, number>;
  readonly m_sceneEntityLoads: Map<string, Promise<void>>;
  applySceneMetadataChanges(scene: unknown): void;
  applyProjectModifiedDate(projectId: string, modifiedAt: string): void;
  getStorageContext(): ThumbnailStorageContext | null;
}
